import { Boxes, Building2, ReceiptText, ShieldCheck, UserSquare2, Users } from 'lucide-react'
import { Navigate } from 'router-v6'
import { ChannelSettings } from 'v2/pages/settings/outbound-configuration'
import { derivePlanFlags } from 'v2/shared/hooks/use-plan'
import { hasRole } from 'v2/shared/utils'
import { createLazyComponent } from 'v2/shared/utils/lazy-utils'
import {
  canViewBilling,
  canViewTrustAndLegal,
  isAdministrator,
} from 'v2/shared/utils/role-based-visibility'
import type { IRoute } from 'v2/types'
import { type RFeatureFlags, UserRole } from 'v2/types/api/auth'
import type { RUserProfile } from 'v2/types/api/settings'
import { type ModulePermissionTypeMap, RbacModules, RbacPermissionTypes } from 'v2/types/rbac'

// Single source of truth for the plan-tier booleans, shared with useUserPlan — so this
// file can't drift from the Enterprise gate the locks read.
const getPlanInfo = (profileData?: RUserProfile) =>
  derivePlanFlags(profileData?.plan, profileData?.organizations?.[0]?.org_created_at)

// Lazy-loaded components
const BillingTabs = createLazyComponent(() => import('v2/pages/billing/billing-tabs'))
const BusinessHoursHolidays = createLazyComponent(
  () => import('v2/pages/settings/business-hours-holidays'),
)
const ConversationSettings = createLazyComponent(() => import('v2/pages/settings/conversations'))
const Destinations = createLazyComponent(() => import('v2/pages/settings/destinations'))
const ListQueues = createLazyComponent(() => import('v2/pages/settings/flows/queues'))
const VerifiedCallerIds = createLazyComponent(
  () => import('v2/pages/settings/forwarding-contacts/verified-caller-ids-page'),
)
const InternalNumbers = createLazyComponent(
  () => import('v2/pages/settings/forwarding-contacts/internal-numbers'),
)
const OrgStatus = createLazyComponent(() => import('v2/pages/settings/org-status'))
const SenderIDs = createLazyComponent(() => import('v2/pages/settings/phone-numbers/sender-ids'))
const Profile = createLazyComponent(() => import('v2/pages/settings/profile/'))
const Skills = createLazyComponent(() => import('v2/pages/settings/skills'))
const TeamsPage = createLazyComponent(() => import('v2/pages/settings/teams'))
const AgentWorkload = createLazyComponent(
  () => import('v2/pages/settings/workload-access/agent-workload'),
)
const ContactViewConfig = createLazyComponent(() => import('v2/pages/settings/contact-view-config'))
const OrganizationSettings = createLazyComponent(
  () => import('v2/pages/settings/organization-settings'),
)
const TrustAndLegal = createLazyComponent(() => import('v2/pages/trust-and-legal'))

export const getSettingsRoutes = (meta?: {
  profileData?: RUserProfile
  featureFlags?: RFeatureFlags
  roles?: UserRole[]
  permissionsMap?: ModulePermissionTypeMap
  // undefined while SMS geo permissions are loading — hide the entry but don't block the route
  hasSenderIdAccess?: boolean
  isIndianRegion?: boolean
}): IRoute[] => {
  const { isFreePlan } = getPlanInfo(meta?.profileData)
  const humanSpecialistEnabled =
    meta?.profileData?.organizations?.[0]?.human_specialist_enabled ?? false
  const canAccessBilling =
    meta?.permissionsMap?.[RbacModules.billing]?.[RbacPermissionTypes.view] && canViewBilling(meta)

  return [
    // Profile routes — trimmed to Profile + 2FA (SER-5859). Organization and
    // API keys moved to the Organization settings page below.
    {
      path: '/profile',
      title: 'User settings',
      icon: <UserSquare2 size={16} />,
      component: <Profile />,
      children: [
        {
          path: '/profile/details',
          title: 'Profile Details',
          component: <Profile />,
          hidden: true,
        },
        {
          path: '/profile/2fa',
          title: '2FA',
          component: <Profile />,
          hidden: true,
        },
      ],
    },
    // Organization and API keys tabs moved to Organization settings (SER-5859)
    {
      path: '/profile/organization',
      title: 'Organization',
      component: <Navigate to="/organization-settings/general" replace />,
      hidden: true,
    },
    {
      path: '/profile/auth',
      title: 'API Keys',
      component: <Navigate to="/organization-settings/api-keys" replace />,
      hidden: true,
    },
    // IP Whitelisting now has its own Organization settings tab (SER-5859)
    {
      path: '/ip-whitelisting',
      title: 'IP allowlist',
      component: <Navigate to="/organization-settings/ip-whitelisting" replace />,
      hidden: true,
    },
    // Organization settings (SER-5859): shallow popover item -> one page, top
    // tabs per sub-route (same URL-sub-route pattern as <Profile /> above).
    // <OrganizationSettings /> reads the active tab from the path itself and
    // gates each tab per-RBAC (see getOrganizationSettingsTabs) — a non-admin
    // hitting a tab they can't see is redirected to their first permitted one.
    {
      path: '/organization-settings',
      title: 'Organization settings',
      icon: <Building2 size={16} />,
      component: <OrganizationSettings />,
      children: [
        {
          path: '/organization-settings/general',
          title: 'Organization',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/api-keys',
          title: 'API keys',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/sso',
          title: 'SAML SSO',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/ip-whitelisting',
          title: 'IP allowlist',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/users',
          title: 'Users',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/sub-accounts',
          title: 'Sub-accounts',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/voice',
          title: 'Voice settings',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/messaging',
          title: 'Messaging settings',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/limits',
          title: 'Account limits',
          component: <OrganizationSettings />,
          hidden: true,
        },
        {
          path: '/organization-settings/business-verification',
          title: 'Business verification',
          component: <OrganizationSettings />,
          hidden: true,
        },
      ],
    },
    // Account Settings — old standalone page, now a redirect (SER-5859); the
    // "Account limits" tab above enforces admin-only via the page itself.
    {
      path: '/account-settings',
      title: 'Account Limits',
      component: <Navigate to="/organization-settings/limits" replace />,
      hidden: true,
    },
    // Old Organization settings "Team setup" tab URL — Team setup was split
    // into a Users tab + a standalone Teams page (SER-5859 follow-up).
    {
      title: 'Team setup',
      path: '/organization-settings/team',
      component: <Navigate to="/organization-settings/users" replace />,
      hidden: true,
    },
    // Team Setup — old bare path + its Users/Teams sub-tabs, all now pure
    // redirects (SER-5859 follow-up): Users moved into Organization settings,
    // Teams moved to its own /teams page (Human Specialist popover). The
    // destination pages/route enforce their own RBAC, same as
    // /account-settings above — no need to duplicate the checks here.
    {
      title: 'Team Setup',
      path: '/team-setup',
      component: <Navigate to="/organization-settings/users" replace />,
      hidden: true,
    },
    {
      title: 'Users',
      path: '/team-setup/users',
      component: <Navigate to="/organization-settings/users" replace />,
      hidden: true,
    },
    {
      title: 'Teams',
      path: '/team-setup/teams',
      component: <Navigate to="/teams" replace />,
      hidden: true,
    },
    // Platform
    {
      title: 'Platform',
      path: '/platform',
      icon: <Boxes size={16} strokeWidth={1.5} />,
      notAllowed:
        !meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
        !meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view] &&
        !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
      hidden:
        !meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
        !meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view] &&
        !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
      children: [
        {
          title: 'SMS Sender IDs',
          path: '/sender-id',
          component: <SenderIDs />,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view] ||
            meta?.hasSenderIdAccess === false,
          hidden:
            !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view] ||
            !meta?.hasSenderIdAccess,
        },
        {
          title: 'Verified Caller IDs',
          path: '/verified-caller-ids',
          component: <VerifiedCallerIds />,
          // Not offered in India. The Phone Numbers tab and the command palette
          // already gate on `!isIndianRegion`; this route never did. Compared
          // against `true` so absent meta fails OPEN.
          notAllowed:
            meta?.isIndianRegion === true ||
            (!meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
              !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view]),
          hidden:
            meta?.isIndianRegion === true ||
            (!meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
              !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view]),
        },
      ],
    },
    // Human Specialist
    {
      title: 'Human Specialist',
      path: '/human-specialist',
      icon: <Users size={16} strokeWidth={1.5} />,
      hidden: !humanSpecialistEnabled,
      notAllowed: !humanSpecialistEnabled,
      children: [
        // Teams (SER-5859 follow-up): moved out of Team Setup's old
        // Users/Teams sub-tabs into its own page, gated on team:view like
        // every other child here — mirrors the Teams item in the Human
        // Specialist popover (human-specialist-popover.tsx).
        {
          title: 'Teams',
          path: '/teams',
          component: <TeamsPage />,
          notAllowed:
            isFreePlan || !meta?.permissionsMap?.[RbacModules.team]?.[RbacPermissionTypes.view],
          hidden:
            isFreePlan || !meta?.permissionsMap?.[RbacModules.team]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Queues',
          path: '/queues',
          component: <ListQueues />,
          notAllowed: !meta?.permissionsMap?.[RbacModules.queue]?.[RbacPermissionTypes.view],
          hidden: !meta?.permissionsMap?.[RbacModules.queue]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Skills',
          path: '/skills',
          component: <Skills />,
          notAllowed: !meta?.permissionsMap?.[RbacModules.skill]?.[RbacPermissionTypes.view],
          hidden: !meta?.permissionsMap?.[RbacModules.skill]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Availability Status',
          path: '/status',
          component: <OrgStatus />,
          notAllowed: !meta?.permissionsMap?.[RbacModules.agentStatus]?.[RbacPermissionTypes.view],
          hidden: !meta?.permissionsMap?.[RbacModules.agentStatus]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Internal Contacts',
          path: '/forwarding-contacts/internal-contacts',
          component: <InternalNumbers />,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
          hidden:
            !meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Outbound Configuration',
          path: '/outbound-configuration',
          component: <ChannelSettings />,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view],
          hidden:
            !meta?.permissionsMap?.[RbacModules.channelCalls]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.channelMessage]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.channelWhatsapp]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Business Hours & Holidays',
          path: '/business-hours-holidays',
          component: <Navigate to="/business-hours-holidays/business-hours" replace />,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.businessHour]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.holiday]?.[RbacPermissionTypes.view],
          hidden:
            !meta?.permissionsMap?.[RbacModules.businessHour]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.holiday]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Business Hours',
          path: '/business-hours-holidays/business-hours',
          component: <BusinessHoursHolidays />,
          hidden: true,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.businessHour]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.holiday]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Holidays',
          path: '/business-hours-holidays/holidays',
          component: <BusinessHoursHolidays />,
          hidden: true,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.businessHour]?.[RbacPermissionTypes.view] &&
            !meta?.permissionsMap?.[RbacModules.holiday]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Workload',
          path: '/workload',
          component: <AgentWorkload />,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.workloadAccessAgent]?.[RbacPermissionTypes.view],
        },
        {
          title: 'Conversations',
          path: '/workload-access/conversations',
          component: <ConversationSettings />,
          notAllowed:
            !meta?.permissionsMap?.[RbacModules.workloadAccessConversations]?.[
              RbacPermissionTypes.view
            ],
        },
        {
          title: 'Contact View Settings',
          path: '/contact-view-config',
          component: <ContactViewConfig />,
          notAllowed: !hasRole(meta, UserRole.administrator),
          hidden: !hasRole(meta, UserRole.administrator),
        },
      ],
    },
    // Destinations
    {
      title: 'Destinations',
      path: '/destinations',
      component: <Destinations />,
      notAllowed: !meta?.permissionsMap?.[RbacModules.destination]?.[RbacPermissionTypes.view],
      hidden: true,
    },
    // Admins with trustAndLegalEnabled. Flags read false until hodor answers, so the route stays
    // open while they load and the page waits; once loaded, the flag closes it.
    {
      path: '/trust-and-legal',
      title: 'Trust & Legal Compliance',
      icon: <ShieldCheck size={16} />,
      component: <TrustAndLegal />,
      notAllowed:
        !isAdministrator(meta) ||
        (!meta?.featureFlags?.isInitialData && !canViewTrustAndLegal(meta)),
      hidden: true,
      children: [
        {
          path: '/trust-and-legal/security-reports',
          title: 'Security Reports',
          component: <TrustAndLegal />,
          hidden: true,
        },
      ],
    },
    // Billing (SER-5859): horizontal in-page tabs — mirrors Organization
    // settings (see pages/billing/billing-tabs.tsx). <BillingTabs/> reads the
    // active tab from the path itself and gates Limits (admin) / Invoices &
    // receipts (paid plan) per-tab — a user landing on a tab/bare /billing
    // they can't see is redirected to the first tab they're permitted to see
    // (Overview). All children below are `hidden: true` and render the same
    // shell, same as organization-settings' children.
    {
      title: 'Billing',
      path: '/billing',
      icon: <ReceiptText size={16} strokeWidth={1.5} />,
      component: <BillingTabs />,
      notAllowed: !canAccessBilling,
      hidden: !canAccessBilling,
      children: [
        {
          title: 'Overview',
          path: '/billing/overview',
          component: <BillingTabs />,
          hidden: true,
        },
        {
          title: 'Payment Methods',
          path: '/billing/payment-methods',
          component: <BillingTabs />,
          hidden: true,
        },
        {
          title: 'Invoices & Receipts',
          path: '/billing/invoice-and-receipt',
          component: <Navigate to="/billing/invoice-and-receipt/invoices" replace />,
          hidden: true,
        },
        {
          title: 'Invoices',
          path: '/billing/invoice-and-receipt/invoices',
          component: <BillingTabs />,
          hidden: true,
        },
        {
          title: 'Receipts',
          path: '/billing/invoice-and-receipt/receipts',
          component: <BillingTabs />,
          hidden: true,
        },
        {
          title: 'Billing information',
          path: '/billing/information',
          component: <BillingTabs />,
          hidden: true,
        },
        {
          title: 'Plans & Pricing',
          path: '/billing/plans-and-pricing',
          component: <BillingTabs />,
          hidden: true,
        },
      ],
    },
  ]
}
